import { useMemo } from "react";
import { exportJsonToExcel } from "../../utils/exports/exportJsontoExcel";
import IOButton from "../Form/Button/IOButton";

export type ExportJsonData = Record<string, unknown>[];

type MetaFields = {
  name: string;
  alias: string;
  label: string;
  type: string;
  description: string | null;
};

type ExportButtonProps = {
  data: ExportJsonData;
  fileName: string;
  label?: string;
  fields: MetaFields[];
  styles?: React.CSSProperties;
};

const NUMERIC_TYPES = ["currency", "percent", "number"];

/** Raw values (no formatter) for export so Excel can store numbers and apply number format (z). */
function transformDataWithLabelsRaw(
  data: ExportJsonData,
  fields: MetaFields[]
): Record<string, unknown>[] {
  if (data.length === 0) return [];

  const columns = Object.keys(data[0] ?? {});
  const fieldByColumn: Record<string, MetaFields> = {};

  for (const f of fields) {
    fieldByColumn[f.name] = f;
    if (f.alias) fieldByColumn[f.alias] = f;
  }

  return data.map((row) =>
    Object.fromEntries(
      columns.map((column) => {
        const field = fieldByColumn[column];
        const label = field?.label ?? column;
        const raw = row[column];
        const value =
          NUMERIC_TYPES.includes(field?.type ?? "") && raw ? Number(raw) : raw;
        return [label, value];
      })
    )
  );
}

export default function ExportButton({
  data,
  fields,
  fileName = "InsightOut",
  label = "Export to Excel",
  styles,
}: ExportButtonProps) {
  const exportData = useMemo(
    () => transformDataWithLabelsRaw(data, fields),
    [data, fields]
  );

  return (
    <IOButton
      onClick={() => {
        exportJsonToExcel(exportData, fileName, fields).catch(() => {
          /* toast already shown by withDownloadToast */
        });
      }}
      smallPadding
      style={{ fontSize: 12, padding: "4px 8px", height: "auto", ...styles }}
      disabled={data.length === 0}
    >
      {label}
    </IOButton>
  );
}
